<template>
  <Modal
    v-show="
      (activeStep <= 2 && !useProfessionalGradLoanOptionsModal) ||
      (useProfessionalGradLoanOptionsModal && activeStep === 1) ||
      !hasRepaymentBasedRates
    "
    class="loan-configuration-modal"
    id="loc-loan-configuration"
    :closeBtn="true"
    @close="closeModal"
  >
    <BorrowAmount
      v-if="activeStep === 1"
      :isCurrent="activeStep === 1"
      @close="$emit('close')"
      @complete="complete"
      :application="application"
      :loading="loading || updatingBorrowAmount"
      :initialDrawRequest="initialDrawRequest"
      :term="term"
    />

    <LoanOptions
      v-if="activeStep === 2 && !isValidProfessionalGradFlow"
      :isCurrent="activeStep === 2"
      :application="application"
      :applicant="applicant"
      :loading="loading"
      :initialDrawAmount="initialDrawRequest?.amount"
      @previous="goBack"
      @complete="complete"
      @close="$emit('close')"
    />

    <PelLoanOptions
      v-if="activeStep === 2 && isValidProfessionalGradFlow && !hasRepaymentBasedRates"
      :loading="loading"
      @previous="goBack"
      @complete="complete"
    ></PelLoanOptions>
  </Modal>
  <PgLoanOptionsModal
    ref="PgLoanOptionsModalRef"
    v-if="activeStep === 2 && useProfessionalGradLoanOptionsModal"
    @configurationComplete="complete"
    @close="$emit('close')"
    :closeBtn="true"
  />
</template>

<script setup lang="ts">
import { ref, watch, onBeforeMount } from "vue";
import { useStore } from "vuex";
import Modal from "@/components/shared/Modal.vue";
import BorrowAmount from "@/components/loan-configuration/BorrowAmount.vue";
import LoanOptions from "@/components/loan-configuration/LoanOptions.vue";
import PgLoanOptionsModal from "@/components/professional-grad/PgLoanOptionsModal.vue";
import PelLoanOptions from "@/components/pel-loan-configuration/PelLoanOptions.vue";
import useApplicationData from "@/composables/useApplicationData";

const PgLoanOptionsModalRef = ref<InstanceType<typeof PgLoanOptionsModal>>();

const store = useStore();

interface Props {
  application: any;
  applicant: any;
  term?: string;
  initialDrawRequest?: any;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "complete", payload: any): void;
  (e: "updateBorrowAmount", payload: { amount: number; requestedAmountChanged: boolean }): void;
}>();

const activeStep = ref<number>(1);
const updatingBorrowAmount = ref<boolean>(false);

const { useProfessionalGradLoanOptionsModal, hasRepaymentBasedRates, isValidProfessionalGradFlow } =
  useApplicationData();

function closeModal() {
  emit("close");
}

function goBack() {
  if (activeStep.value === 2) {
    activeStep.value -= 1;
  }
}

function borrowAmountComplete(payload: { amount: number; requestedAmountChanged: boolean }) {
  if (payload.amount) {
    updatingBorrowAmount.value = true;
    emit("updateBorrowAmount", payload);
  } else {
    next();
  }
}

function complete(payload?: any) {
  if (activeStep.value === 1) {
    borrowAmountComplete(payload);
  } else {
    emit("complete", payload);
  }
}

function next() {
  if (activeStep.value < 2) {
    activeStep.value++;
  }
}

watch(
  () => updatingBorrowAmount.value,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      next();
    }
  },
);

onBeforeMount(() => {
  // check if school is undecided
  if (store.state.appData?.application?.schoolConfiguration?.schoolUndecided) {
    updatingBorrowAmount.value = false;
    activeStep.value = 2;
  }
});

defineExpose({
  updatingBorrowAmount,
	PgLoanOptionsModalRef,
});
</script>

<style>
.loan-configuration-modal#loc-loan-configuration .modal-container {
  width: 95vw;
  max-width: none;
}

.loan-configuration-modal#loc-loan-configuration .btn.btn-primary {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
}

.loan-configuration-modal .step-btns {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  width: 100%;
}

.loan-configuration-modal .step-btns button:not(:first-child) {
  margin-left: 10px;
}

.loan-configuration-modal .step-btns button {
  min-width: 110px;
  height: 34px;
  font-size: 0.875rem;
  font-weight: 600;
}

.loan-configuration-modal .step-content .content {
  width: 100%;
}

.loan-configuration-modal .modal-body {
  overflow-x: hidden;
}

@media (max-width: 992px) {
  .loan-configuration-modal .step-btns {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  .loan-configuration-modal .modal-body {
    overflow-x: auto;
  }
}
</style>
